import { css, keyframes } from '@emotion/react'
import { Set as ISet } from 'immutable'
import React, {
  type AriaAttributes,
  type ComponentProps,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { AutoSizer, List, WindowScroller } from 'react-virtualized'

import { UnstyledAnchor } from '../../components/UnstyledAnchor'
import { UnstyledButton } from '../../components/UnstyledButton'
import type { ReactAnchorProps, TRestrictedButtonProps } from '../../types'
import { CssVars } from '../themes'
import { FoundationIcon } from './FoundationIcon'
import { Spinner16 } from './Spinner'
import { _InternalTextColorOverride } from './TextColorOverride'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'

type SortDirection = 'ascending' | 'descending'
type SelectionState = 'all' | 'some' | 'none'
type SortProp<TColumnKey extends string> = {
  direction: SortDirection
  column: TColumnKey
}
type RowAnchorProps = Omit<
  ReactAnchorProps,
  'ref' | 'onMouseEnter' | 'onMouseLeave'
>
type RowAction = {
  type: 'button'
  icon: ComponentProps<typeof FoundationIcon>['type']
  onClick: () => void
} & TRestrictedButtonProps

type Row<TRowKey extends string> = {
  key: TRowKey
  content: Array<ReactNode>
  action?: RowAction
} & RowAnchorProps

type Column<TKey extends string> = {
  label: string
  width?: number
  sortKey?: TKey
}

type SelectionInfo<TRowKey extends string> = {
  selectedKeys: ISet<TRowKey>
  onSelectKeys: (selected: ISet<TRowKey>) => void
  onDeselectKeys: (deselected: ISet<TRowKey>) => void
  onSelectAll: () => void
  onDeselectAll: () => void
}

type Props<TColumnKey extends string, TRowKey extends string> = {
  state?: 'default' | 'loading'
  sort?: SortProp<TColumnKey> | null
  onSort?: (column: TColumnKey) => void
  columns: Array<Column<TColumnKey>>
  rows: Array<Row<TRowKey>>

  selection?: SelectionInfo<TRowKey> | null

  headerStickyTop?: number
  overrideRowHeight?: number
}

/**
 * This component renders a table whose rows are [anchors](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a).
 */
export function AnchorTable<TColumnKey extends string, TRowKey extends string>({
  columns,
  headerStickyTop,
  onSort,
  overrideRowHeight,
  rows,
  selection,
  sort = null,
  state,
}: Props<TColumnKey, TRowKey>) {
  const {
    actionButtonSize,
    rowHeight: themeRowHeight,
    stickyHeaderZIndex,
  } = getThemeVars()
  const rowHeight = overrideRowHeight ?? themeRowHeight
  const isInSelectMode = !!selection
  const widths = getColumnWidths({ actionButtonSize, columns })
  let selectionState: SelectionState | null = null
  if (selection) {
    if (selection.selectedKeys.isEmpty()) {
      selectionState = 'none'
    } else if (selection.selectedKeys.count() === rows.length) {
      selectionState = 'all'
    } else {
      selectionState = 'some'
    }
  }

  const renderRow = ({
    index,
    style,
  }: {
    index: number
    style: React.CSSProperties
  }) => {
    const row = rows[index]
    return (
      <div key={row.key} style={style}>
        <Row
          height={rowHeight}
          isInSelectMode={!!selection}
          isSelected={!!selection && selection.selectedKeys.has(row.key)}
          onDeselect={() => {
            if (selection) {
              selection.onDeselectKeys(ISet.of(row.key))
            }
          }}
          onSelect={() => {
            if (selection) {
              selection.onSelectKeys(ISet.of(row.key))
            }
          }}
          row={row}
          widths={widths}
        />
      </div>
    )
  }

  return (
    <WindowScroller>
      {({ height, scrollTop }) => (
        <div
          css={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            width: '100%',
          }}
          role="table"
        >
          <div
            css={
              headerStickyTop !== undefined && {
                position: 'sticky',
                top: headerStickyTop,
                // NOTE(jare): cast to any since we're using a CSS variable string, but
                // emotion expects a number
                zIndex: stickyHeaderZIndex as any,
              }
            }
            role="rowgroup"
          >
            <HeaderRow
              columns={columns}
              isInSelectMode={isInSelectMode}
              onDeselectAll={() => {
                if (selection) {
                  selection.onDeselectAll()
                }
              }}
              onSelectAll={() => {
                if (selection) {
                  selection.onSelectAll()
                }
              }}
              onSort={onSort}
              selectionState={selectionState}
              sort={sort}
              widths={widths}
            />
          </div>
          {rows.length === 0 && state === 'loading' ? (
            // TODO(jare): Make this number configurable
            <LoadingBody numRows={40} rowHeight={rowHeight} />
          ) : (
            <div css={{ position: 'relative' }}>
              {state === 'loading' && (
                <div
                  css={{
                    alignItems: 'center',
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    maxHeight: 250,
                    position: 'absolute',
                    width: '100%',
                  }}
                >
                  <Spinner16 />
                </div>
              )}
              <AutoSizer disableHeight>
                {({ width }) => (
                  <div
                    css={[
                      { transition: 'opacity 300ms' },
                      state === 'loading' && {
                        opacity: 0.2,
                        pointerEvents: 'none',
                      },
                    ]}
                    role="rowgroup"
                  >
                    <List
                      autoHeight
                      height={height}
                      overscanRowCount={5}
                      rowCount={rows.length}
                      rowHeight={rowHeight}
                      rowRenderer={renderRow}
                      scrollTop={scrollTop}
                      width={width}
                    />
                  </div>
                )}
              </AutoSizer>
            </div>
          )}
        </div>
      )}
    </WindowScroller>
  )
}

function HeaderRow<TKey extends string>({
  columns,
  isInSelectMode,
  onDeselectAll,
  onSelectAll,
  onSort,
  selectionState,
  sort,
  widths,
}: {
  columns: Array<Column<TKey>>
  sort: SortProp<TKey> | null
  onSort?: (column: TKey) => void
  widths: Array<string>
  isInSelectMode: boolean
  onSelectAll: () => void
  onDeselectAll: () => void
  selectionState: SelectionState | null
}) {
  const { headerBackgroundColor, rowGutterWidth } = getThemeVars()
  return (
    <div
      css={{
        backgroundColor: headerBackgroundColor,

        display: 'flex',
        // NOTE(jare): stretch this to the same width as the rows. That way, if this
        // header's sticky, the edges of the rows aren't visible underneath the header
        // as the header is scrolled over them.
        margin: `0 calc(-1 * ${rowGutterWidth})`,
        padding: `0 ${rowGutterWidth}`,
      }}
      role="row"
    >
      {columns.map((column, index) => {
        return (
          <HeaderCell
            key={index}
            column={column}
            isFirstColumn={index === 0}
            isInSelectMode={isInSelectMode}
            isLastColumn={index === columns.length - 1}
            onDeselectAll={onDeselectAll}
            onSelectAll={onSelectAll}
            onSort={onSort}
            selectionState={selectionState}
            sort={sort}
            width={widths[index]}
          />
        )
      })}
    </div>
  )
}

function HeaderCell<TKey extends string>({
  column,
  isFirstColumn,
  isInSelectMode,
  isLastColumn,
  onDeselectAll,
  onSelectAll,
  onSort,
  selectionState,
  sort,
  width,
}: {
  column: Column<TKey>
  sort: SortProp<TKey> | null
  onSort?: (column: TKey) => void
  width: string
  isFirstColumn: boolean
  isLastColumn: boolean
  isInSelectMode: boolean
  onSelectAll: () => void
  onDeselectAll: () => void
  selectionState: SelectionState | null
}) {
  const { label } = column
  const {
    borderColor,
    cellPaddingRight,
    checkboxGutterWidth,
    headerHeight,
    labelColor,
  } = getThemeVars()

  const isSorted =
    !!sort && 'sortKey' in column && sort.column === column.sortKey

  const checkboxWrapperRef = useRef<HTMLDivElement | null>(null)

  const checkboxNode =
    isFirstColumn && isInSelectMode ? (
      <div
        ref={checkboxWrapperRef}
        css={{ display: 'flex', marginRight: checkboxGutterWidth }}
      >
        <HeaderCheckbox
          onDeselectAll={onDeselectAll}
          onSelectAll={onSelectAll}
          selectionState={selectionState}
        />
      </div>
    ) : null

  return (
    <div
      key={label}
      css={{
        alignItems: 'center',
        borderBottom: `1px solid ${borderColor}`,
        boxSizing: 'border-box',
        display: 'flex',
        height: headerHeight,
        paddingRight: cellPaddingRight,
        position: 'relative',
        textAlign: 'left',
        width,
      }}
      role="columnheader"
    >
      {onSort && !!column.sortKey ? (
        <>
          <HeaderSortableCell
            checkboxWrapperEl={checkboxWrapperRef.current}
            includeCheckboxSpace={!!checkboxNode}
            isLastColumn={isLastColumn}
            isSorted={isSorted}
            label={label}
            onSort={() => column.sortKey && onSort(column.sortKey)}
            sort={sort}
          />
          <div css={{ display: 'flex', left: 0, position: 'absolute' }}>
            {checkboxNode}
          </div>
        </>
      ) : (
        <div
          css={{
            alignItems: 'center',
            borderBottom: '2px solid transparent',
            display: 'flex',
            height: '100%',
            width: '100%',
          }}
        >
          {checkboxNode}
          <_InternalTextColorOverride textColor={labelColor}>
            {({ rootClassName }) => (
              <span className={rootClassName}>
                <Type.Text size="S">{label}</Type.Text>
              </span>
            )}
          </_InternalTextColorOverride>
        </div>
      )}
    </div>
  )
}

function HeaderCheckbox({
  onDeselectAll,
  onSelectAll,
  selectionState,
}: {
  selectionState: SelectionState | null
  onSelectAll: () => void
  onDeselectAll: () => void
}) {
  let checkboxState: ComponentProps<typeof InternalCheckbox>['state']
  let ariaChecked: AriaAttributes['aria-checked']
  switch (selectionState) {
    case 'all':
      checkboxState = 'checked'
      ariaChecked = 'true'
      break
    case 'some':
      checkboxState = 'indeterminate'
      ariaChecked = 'mixed'
      break
    default:
      checkboxState = 'unchecked'
      ariaChecked = 'false'
      break
  }
  return (
    <UnstyledButton
      aria-checked={ariaChecked}
      onClick={() => {
        if (selectionState === 'all') {
          onDeselectAll()
        } else {
          onSelectAll()
        }
      }}
      // TODO(jare): Come up with a good way to label this checkbox
      role="checkbox"
    >
      <InternalCheckbox state={checkboxState} />
    </UnstyledButton>
  )
}

function HeaderSortableCell<TKey extends string>({
  checkboxWrapperEl,
  includeCheckboxSpace,
  isLastColumn,
  isSorted,
  label,
  onSort,
  sort,
}: {
  label: string
  sort: SortProp<TKey> | null
  onSort: () => void
  isSorted: boolean
  checkboxWrapperEl: HTMLElement | null
  includeCheckboxSpace: boolean
  isLastColumn: boolean
}) {
  const {
    checkboxGutterWidth,
    headerGutterWidth,
    headerHoverColor,
    labelColor,
  } = getThemeVars()

  const checkboxSpace = includeCheckboxSpace
    ? `calc(${_INTERNAL_CHECKBOX_SIZE}px + ${checkboxGutterWidth})`
    : 0

  const [isHovered, setIsHovered] = useState(false)
  return (
    <_InternalTextColorOverride
      textColor={isSorted || isHovered ? headerHoverColor : labelColor}
    >
      {({ rootClassName }) => (
        <UnstyledButton
          className={rootClassName}
          css={[
            {
              alignItems: 'center',
              borderBottom: '2px solid transparent',
              cursor: 'pointer',
              display: 'flex',
              height: '100%',
              marginBottom: '-1px',
              position: 'relative',
              textAlign: 'left',
              width: isLastColumn
                ? '100%'
                : `calc(100% - ${headerGutterWidth})`,
            },
            isSorted && {
              borderColor: headerHoverColor,
            },
          ]}
          onClick={e => {
            if (
              // If the user was trying to click the checkbox, then cut out early
              // so we don't sort the column
              checkboxWrapperEl &&
              checkboxWrapperEl.contains(e.target as HTMLElement)
            ) {
              return
            }

            onSort()
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* NOTE(jare): Render bold font in the background to ensure that we reserve enough
            space for a bolder label. This prevents the layout from shifting when the user
            switches this column from sorted to unsorted or vice versa. */}
          <div
            css={{
              alignItems: 'center',
              display: 'flex',
              paddingLeft: checkboxSpace,
              visibility: 'hidden',
            }}
          >
            <Type.Text isBold size="S">
              {label}
            </Type.Text>
          </div>
          <div
            css={{
              alignItems: 'center',
              display: 'flex',
              paddingLeft: checkboxSpace,
              position: 'absolute',
            }}
          >
            <Type.Text isBold={isSorted} size="S">
              {label}
            </Type.Text>
          </div>
          {isSorted && sort && <HeaderCellArrow direction={sort.direction} />}
        </UnstyledButton>
      )}
    </_InternalTextColorOverride>
  )
}

const sortArrowEntranceKeyframes = keyframes`
  0% {
    opacity: 0;
    transform: translate(-4px, 0);
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translate(0, 0);
  }
`

function HeaderCellArrow({ direction }: { direction: SortDirection }) {
  return (
    <div
      css={{
        animationDuration: '300ms',
        animationName: sortArrowEntranceKeyframes.toString(),
        position: 'absolute',
        right: 0,
      }}
    >
      <FoundationIcon
        type={direction === 'ascending' ? 'CaretUp12' : 'CaretDown12'}
      />
    </div>
  )
}

function Row<TKey extends string>({
  height,
  isInSelectMode,
  isSelected,
  onDeselect,
  onSelect,
  row,
  widths,
}: {
  row: Row<TKey>
  widths: Array<string>
  isSelected: boolean
  onSelect?: () => void
  onDeselect?: () => void
  isInSelectMode: boolean
  height: number
}) {
  const {
    borderColor,
    cellPaddingRight,
    cellTextColor,
    checkboxGutterWidth,
    rowGutterWidth,
    rowHoverColor,
  } = getThemeVars()
  const [isRowHovered, setIsRowHovered] = useState(false)
  const { action, content, key, ...anchorProps } = row

  const handleMouseEnter = () => {
    setIsRowHovered(true)
  }
  const handleMouseLeave = () => {
    setIsRowHovered(false)
  }

  const innerNode = (
    <div
      css={[
        {
          borderBottom: `1px solid ${borderColor}`,
          boxSizing: 'border-box',
          display: 'flex',
          height,
        },
        isRowHovered && {
          borderColor: 'transparent',
        },
      ]}
    >
      {content.map((c, index) => {
        const width = widths[index]
        return (
          <div
            key={index}
            css={{
              alignItems: 'center',
              boxSizing: 'border-box',
              display: 'flex',
              height: '100%',
              minWidth: 0,
              paddingRight: cellPaddingRight,
              width,
            }}
            role="cell"
          >
            {isInSelectMode && index === 0 && (
              <div css={{ display: 'flex', marginRight: checkboxGutterWidth }}>
                <InternalCheckbox
                  state={isSelected ? 'checked' : 'unchecked'}
                />
              </div>
            )}
            <div
              css={{
                // NOTE(jare): We need to apply the cell's text color to this div to make
                // sure that the ellipsis that gets rendered when the text overflows is the
                // right color.
                color: cellTextColor,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {c}
            </div>
          </div>
        )
      })}
    </div>
  )

  const wrapperStyles = css([
    {
      // NOTE(jare): We set the box model to content box explicitly here since
      // we want the extra padding to stretch this row beyond the bounds of its
      // container
      boxSizing: 'content-box',
      cursor: 'pointer',
      display: 'block',
      margin: `0 calc(-1 * ${rowGutterWidth})`,
      padding: `0 ${rowGutterWidth}`,
      width: '100%',
    },
    isRowHovered && {
      backgroundColor: rowHoverColor,
    },
  ])

  if (isInSelectMode) {
    return (
      <UnstyledButton
        aria-checked={isSelected ? 'true' : 'false'}
        css={wrapperStyles}
        onClick={() => {
          if (!isSelected && onSelect) {
            onSelect()
          } else if (isSelected && onDeselect) {
            onDeselect()
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="row checkbox"
      >
        {innerNode}
      </UnstyledButton>
    )
  }

  return (
    <div css={{ alignItems: 'center', display: 'flex', position: 'relative' }}>
      <UnstyledAnchor
        {...anchorProps}
        css={wrapperStyles}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="row"
      >
        {innerNode}
      </UnstyledAnchor>
      {action && (
        // NOTE(jare): We render the row action on top of the anchor instead of inside
        // it to avoid rendering an interactive element inside of an anchor tag
        <div css={{ position: 'absolute', right: 0 }}>
          <RowAction action={action} />
        </div>
      )}
    </div>
  )
}

function RowAction({ action }: { action: RowAction }) {
  const { actionButtonSize } = getThemeVars()
  const [isHovered, setIsHovered] = useState(false)
  if (action.type === 'button') {
    const { icon, type, ...spreadProps } = action
    const buttonPadding = 12
    return (
      <UnstyledButton
        {...spreadProps}
        css={{
          alignItems: 'center',
          boxSizing: 'border-box',
          cursor: 'pointer',
          display: 'flex',
          height: actionButtonSize,
          justifyContent: 'center',
          marginRight: `calc(-1 * ${buttonPadding})`,
          padding: buttonPadding,
          width: actionButtonSize,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <_InternalTextColorOverride
          textColor={
            isHovered ? CssVars.semantic.primary.base : CssVars.neutral.fg1
          }
        >
          {({ rootClassName }) => (
            <span className={rootClassName}>
              <FoundationIcon type={action.icon} />
            </span>
          )}
        </_InternalTextColorOverride>
      </UnstyledButton>
    )
  }

  return null
}

function LoadingBody({
  numRows,
  rowHeight,
}: {
  numRows: number
  rowHeight: number
}) {
  const maskId = useUniqueId()
  const gradientId = useUniqueId()

  const nodes: Array<ReactNode> = []

  const barHeight = 40
  const padding = (rowHeight - barHeight) / 2
  for (let i = 0; i < numRows; i += 1) {
    nodes.push(
      <rect
        key={i}
        fill="#fff"
        height={barHeight}
        style={{
          transform: `translate(0, ${rowHeight * i + padding}px)`,
        }}
        width="100%"
        x="0"
        y="0"
      />
    )
  }

  return (
    <svg css={{ height: numRows * rowHeight, width: '100%' }}>
      <defs>
        <AnimatedGradient id={gradientId} />
      </defs>
      <mask id={maskId}>{nodes}</mask>
      <rect
        fill={`url(#${gradientId})`}
        height="100%"
        mask={`url(#${maskId})`}
        width="100%"
        x="0"
        y="0"
      />
    </svg>
  )
}

function AnimatedGradient({ id }: { id: string }) {
  const { loadingRowBarColor1, loadingRowBarColor2 } = getThemeVars()
  const [offsetPercent, setOffsetPercent] = useState(50)

  const ref = useRef<number>(undefined)
  useEffect(() => {
    const animate = () => {
      setOffsetPercent(prev => {
        const next = prev + 2
        // wrap around when we're about to hit 100%
        return next >= 100 ? 0 : next
      })
      window.requestAnimationFrame(animate)
    }
    ref.current = window.requestAnimationFrame(animate)
    return () => {
      if (ref.current) {
        window.cancelAnimationFrame(ref.current)
      }
    }
  }, [])

  return (
    <linearGradient
      gradientTransform="rotate(45)"
      // NOTE(jare): With the "reflect" spread method, the gradient will be mirrored
      // across the left and right boundaries we define with x1 and x2. This means
      // we can animate those boundaries to shift the gradient over by some offset.
      // This also lets the gradient extend over its edge after it's been rotated.
      // Otherwise, the gradient would stop in the middle of the node it's being
      // applied to, since the rotated edge cuts into the shape.
      id={id}
      spreadMethod="reflect"
      x1={`${offsetPercent}%`}
      x2={`${offsetPercent + 100}%`}
    >
      <stop offset="0%" stopColor={loadingRowBarColor2} />
      <stop offset="50%" stopColor={loadingRowBarColor1} />
      <stop offset="100%" stopColor={loadingRowBarColor2} />
    </linearGradient>
  )
}

function getThemeVars() {
  return {
    actionButtonSize: 36,
    borderColor: CssVars.neutral.bg3,
    cellPaddingRight: `calc(2 * ${CssVars.size.gutterWidth})`,
    cellTextColor: CssVars.textColor,
    checkboxGutterWidth: CssVars.size.gutterWidth,
    headerBackgroundColor: CssVars.neutral.bg1,
    headerGutterWidth: CssVars.size.gutterWidthSm,
    headerHeight: CssVars.size.cellHeight,
    headerHoverColor: CssVars.semantic.primary.base,
    labelColor: CssVars.neutral.fg2,
    loadingRowBarColor1: CssVars.neutral.bg2,
    loadingRowBarColor2: CssVars.neutral.bg3,
    rowGutterWidth: `calc(2 * ${CssVars.size.gutterWidth})`,
    rowHeight: 60,
    rowHoverColor: CssVars.neutral.bg2,
    stickyHeaderZIndex: CssVars.size._experimentalAnchorTableStickyHeaderZIndex,
  }
}

function getColumnWidths<TKey extends string>({
  actionButtonSize,
  columns,
}: {
  columns: Array<Column<TKey>>
  actionButtonSize: number
}): Array<string> {
  const usedWidth =
    actionButtonSize + columns.reduce((sum, col) => sum + (col.width || 0), 0)
  const numColumnsWithoutWidth = columns.reduce(
    (sum, col) => sum + (col.width === undefined ? 1 : 0),
    0
  )

  return columns.map((column, index) => {
    // NOTE(jare): Add the space allocated for the action button to the last column. We do
    // this regardless of whether or not the table has action buttons. Before the table loads,
    // we don't know if it'll have actions. If we return a different width when the table's loading
    // than we do when it's finished loading, it's possible that the table headers will shift after
    // when the loading state changes.
    const actionButtonSpace =
      index === columns.length - 1 ? actionButtonSize : 0

    if (column.width !== undefined) {
      return `${column.width + actionButtonSpace}px`
    }

    return `calc(${actionButtonSpace}px + ((100% - ${usedWidth}px) / ${numColumnsWithoutWidth}))`
  })
}

const _INTERNAL_CHECKBOX_SIZE = 16

function InternalCheckbox({
  state,
}: {
  // TODO(jare): Add disabled states
  state: 'checked' | 'unchecked' | 'indeterminate'
}) {
  return (
    <div
      css={{
        alignItems: 'center',
        display: 'flex',
        height: _INTERNAL_CHECKBOX_SIZE,
        justifyContent: 'center',
        width: _INTERNAL_CHECKBOX_SIZE,
      }}
    >
      <div
        css={[
          {
            border: `1px solid ${
              state === 'unchecked'
                ? CssVars.neutral.fg3
                : CssVars.semantic.primary.base
            }`,
            borderRadius: 2,
            boxSizing: 'border-box',
            height: _INTERNAL_CHECKBOX_SIZE - 2,
            width: _INTERNAL_CHECKBOX_SIZE - 2,
          },
          state !== 'unchecked' && {
            background: CssVars.semantic.primary.base,
          },
        ]}
      >
        {/* TODO(jare): Add checkmark + minus icon */}
      </div>
    </div>
  )
}
